import type {
  ExpressionSpecification,
  LayerSpecification,
  SourceSpecification,
  StyleSpecification,
} from "maplibre-gl"

import { tokens } from "@/lib/tokens.generated"

/*
 * THE MAP — an instrument drawn in the palette (Master Design System §09).
 *
 * The basemap is stone paper with data drawn on it. Roads are two-body — an
 * ink casing (the wall) around a paper core — and never a hue; only the
 * biggest carry say.200 warmth. Colour sits behind, never in: capability
 * tints wash under landuse polygons at ≤16%. Water is the one place pale
 * print exists. Its dashes are cartographic notation, exempt from the UI
 * certainty grammar.
 *
 * Every colour comes from the `map` token group; the alphas the style needs
 * (casings at 8/11/14%, the landuse whispers) are computed here from those
 * bases rather than written down twice.
 */

const map = tokens.color.map

const hexToRgb = (hex: string): [number, number, number] => {
  const m = /^#([0-9a-f]{6})$/i.exec(hex)
  if (!m)
    throw new Error(`the map style needs a six-digit hex base, got ${hex}`)
  const n = parseInt(m[1], 16)
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255]
}

const alpha = (hex: string, a: number) => {
  const [r, g, b] = hexToRgb(hex)
  return `rgba(${r},${g},${b},${a})`
}

/** Ink at an alpha: casings, hairlines, labels. */
const INK = (a: number) => alpha(tokens.color.ink[950], a)

/** Linear over zoom. */
const Z = (
  z1: number,
  v1: number,
  z2: number,
  v2: number,
): ExpressionSpecification => [
  "interpolate",
  ["linear"],
  ["zoom"],
  z1,
  v1,
  z2,
  v2,
]

const NAME: ExpressionSpecification = [
  "coalesce",
  ["get", "name:latin"],
  ["get", "name"],
]

const inClass = (classes: string[]): ExpressionSpecification => [
  "in",
  ["get", "class"],
  ["literal", classes],
]

const linesOf = (classes: string[]): ExpressionSpecification => [
  "all",
  ["==", ["geometry-type"], "LineString"],
  inClass(classes),
]

/** A two-body road: an ink casing under a paper core. */
const road = (
  id: string,
  classes: string[],
  casing: string,
  casingWidth: ExpressionSpecification,
  core: string,
  coreWidth: ExpressionSpecification,
): LayerSpecification[] => [
  {
    id: `${id}-case`,
    type: "line",
    source: "omt",
    "source-layer": "transportation",
    filter: linesOf(classes),
    layout: { "line-cap": "butt", "line-join": "round" },
    paint: { "line-color": casing, "line-width": casingWidth },
  },
  {
    id,
    type: "line",
    source: "omt",
    "source-layer": "transportation",
    filter: linesOf(classes),
    layout: { "line-cap": "round", "line-join": "round" },
    paint: { "line-color": core, "line-width": coreWidth },
  },
]

const TILES = "https://tiles.stadiamaps.com"

/**
 * The ground style: Stadia's OpenMapTiles vectors, drawn in the palette.
 * `extraSources` and `topLayers` let a page add its own drawn data (the drive
 * from the office) between the roads and the labels.
 */
export function groundStyle(
  apiKey: string,
  extraSources: Record<string, SourceSpecification> = {},
  topLayers: LayerSpecification[] = [],
): StyleSpecification {
  const ground: LayerSpecification[] = [
    { id: "bg", type: "background", paint: { "background-color": map.ground } },
    {
      id: "green",
      type: "fill",
      source: "omt",
      "source-layer": "landcover",
      filter: inClass(["grass", "wood", "scrub", "farmland"]),
      paint: {
        "fill-color": [
          "match",
          ["get", "class"],
          "wood",
          map.wood,
          map.landcover,
        ],
        "fill-opacity": 0.85,
      },
    },
    {
      id: "park",
      type: "fill",
      source: "omt",
      "source-layer": "park",
      filter: ["==", ["geometry-type"], "Polygon"],
      paint: { "fill-color": map.landcover },
    },
    {
      id: "park-edge",
      type: "line",
      source: "omt",
      "source-layer": "park",
      minzoom: 13,
      filter: ["==", ["geometry-type"], "Polygon"],
      paint: {
        "line-color": INK(0.15),
        "line-width": 0.7,
        "line-dasharray": [2, 2.8],
      },
    },
    // landuse whispers — colour behind, never in
    {
      id: "school",
      type: "fill",
      source: "omt",
      "source-layer": "landuse",
      filter: inClass(["school", "college", "university", "kindergarten"]),
      paint: { "fill-color": alpha(tokens.color.know[500], 0.16) },
    },
    {
      id: "commerce",
      type: "fill",
      source: "omt",
      "source-layer": "landuse",
      filter: inClass(["commercial", "retail"]),
      paint: { "fill-color": alpha(tokens.color.say[500], 0.08) },
    },
    {
      id: "recreation",
      type: "fill",
      source: "omt",
      "source-layer": "landuse",
      filter: inClass(["pitch", "stadium", "playground", "track"]),
      paint: { "fill-color": alpha(tokens.color.do[500], 0.12) },
    },
    {
      id: "water",
      type: "fill",
      source: "omt",
      "source-layer": "water",
      paint: { "fill-color": map.water },
    },
    {
      id: "waterway",
      type: "line",
      source: "omt",
      "source-layer": "waterway",
      paint: { "line-color": map.water, "line-width": Z(10, 1, 16, 4.5) },
    },
    {
      id: "building",
      type: "fill",
      source: "omt",
      "source-layer": "building",
      minzoom: 14,
      paint: { "fill-color": INK(0.05) },
    },
    {
      id: "building-line",
      type: "line",
      source: "omt",
      "source-layer": "building",
      minzoom: 15.5,
      paint: { "line-color": INK(0.09), "line-width": 0.6 },
    },
    {
      id: "path",
      type: "line",
      source: "omt",
      "source-layer": "transportation",
      minzoom: 15,
      filter: linesOf(["path", "track", "pedestrian"]),
      paint: {
        "line-color": INK(0.28),
        "line-width": 1,
        "line-dasharray": [1.2, 2.4],
      },
    },
  ]

  const roads: LayerSpecification[] = [
    ...road(
      "road-minor",
      ["minor", "service"],
      INK(0.08),
      Z(13, 1.3, 18, 15),
      map["road-core"],
      Z(13, 0.7, 18, 12),
    ),
    ...road(
      "road-mid",
      ["secondary", "tertiary"],
      INK(0.11),
      Z(11, 1.7, 18, 20),
      map["road-core"],
      Z(11, 1, 18, 16),
    ),
    ...road(
      "road-major",
      ["primary", "trunk", "motorway"],
      INK(0.14),
      Z(10, 2.4, 18, 26),
      map["road-major"],
      Z(10, 1.4, 18, 21),
    ),
    {
      id: "rail-case",
      type: "line",
      source: "omt",
      "source-layer": "transportation",
      filter: ["==", ["get", "class"], "rail"],
      paint: { "line-color": map["road-core"], "line-width": Z(11, 3, 17, 8) },
    },
    {
      id: "rail",
      type: "line",
      source: "omt",
      "source-layer": "transportation",
      filter: ["==", ["get", "class"], "rail"],
      paint: {
        "line-color": map.rail,
        "line-width": Z(11, 1.3, 17, 2.4),
        "line-dasharray": [4, 2.5],
      },
    },
    {
      id: "transit",
      type: "line",
      source: "omt",
      "source-layer": "transportation",
      minzoom: 13,
      filter: ["==", ["get", "class"], "transit"],
      paint: {
        "line-color": map.transit,
        "line-width": 1.2,
        "line-dasharray": [2, 2.4],
      },
    },
    {
      id: "boundary",
      type: "line",
      source: "omt",
      "source-layer": "boundary",
      filter: [
        "all",
        ["<=", ["get", "admin_level"], 6],
        ["==", ["get", "maritime"], 0],
      ],
      paint: {
        "line-color": INK(0.13),
        "line-width": 1,
        "line-dasharray": [5, 3],
      },
    },
  ]

  const labels: LayerSpecification[] = [
    {
      id: "road-label",
      type: "symbol",
      source: "omt",
      "source-layer": "transportation_name",
      minzoom: 14,
      filter: inClass([
        "motorway",
        "trunk",
        "primary",
        "secondary",
        "tertiary",
        "minor",
      ]),
      layout: {
        "symbol-placement": "line",
        "text-field": NAME,
        "text-font": ["Noto Sans Regular"],
        "text-size": Z(14, 9, 17, 11.5),
        "text-transform": "uppercase",
        "text-letter-spacing": 0.08,
      },
      paint: {
        "text-color": INK(0.46),
        "text-halo-color": map.ground,
        "text-halo-width": 1.2,
      },
    },
    {
      id: "water-label",
      type: "symbol",
      source: "omt",
      "source-layer": "water_name",
      filter: ["==", ["geometry-type"], "LineString"],
      layout: {
        "symbol-placement": "line",
        "text-field": NAME,
        "text-font": ["Noto Sans Regular"],
        "text-size": 10,
        "text-transform": "uppercase",
        "text-letter-spacing": 0.16,
      },
      paint: {
        "text-color": INK(0.5),
        "text-halo-color": map.water,
        "text-halo-width": 1.1,
      },
    },
    {
      id: "place-suburb",
      type: "symbol",
      source: "omt",
      "source-layer": "place",
      minzoom: 11,
      filter: inClass([
        "suburb",
        "neighbourhood",
        "quarter",
        "village",
        "hamlet",
      ]),
      layout: {
        "text-field": NAME,
        "text-font": ["Noto Sans Medium"],
        "text-size": Z(11, 10, 15, 12),
        "text-transform": "uppercase",
        "text-letter-spacing": 0.14,
        "text-padding": 8,
      },
      paint: {
        "text-color": INK(0.58),
        "text-halo-color": map.ground,
        "text-halo-width": 1.4,
      },
    },
    {
      id: "place-city",
      type: "symbol",
      source: "omt",
      "source-layer": "place",
      filter: inClass(["city", "town"]),
      layout: {
        "text-field": NAME,
        "text-font": ["Noto Sans Medium"],
        "text-size": ["match", ["get", "class"], "city", 14.5, 13],
        "text-letter-spacing": 0.02,
        "text-padding": 8,
      },
      paint: {
        "text-color": INK(0.7),
        "text-halo-color": map.ground,
        "text-halo-width": 1.5,
      },
    },
  ]

  return {
    version: 8,
    glyphs: `${TILES}/fonts/{fontstack}/{range}.pbf?api_key=${apiKey}`,
    sources: {
      omt: {
        type: "vector",
        url: `${TILES}/data/openmaptiles.json?api_key=${apiKey}`,
        attribution:
          "© Stadia Maps © OpenMapTiles © OpenStreetMap contributors",
      },
      ...extraSources,
    },
    layers: [...ground, ...roads, ...topLayers, ...labels],
  }
}

/** The drive from the office to the job: a dashed print line on a paper casing. */
export function routeLayers(): LayerSpecification[] {
  return [
    {
      id: "route-case",
      type: "line",
      source: "route",
      layout: { "line-cap": "round" },
      paint: {
        "line-color": map["road-core"],
        "line-width": 4,
        "line-opacity": 0.65,
      },
    },
    {
      id: "route",
      type: "line",
      source: "route",
      layout: { "line-cap": "round" },
      paint: {
        "line-color": map.route,
        "line-width": 1.8,
        "line-dasharray": [1.5, 2.4],
      },
    },
  ]
}
